import React from 'react';
import { auth } from '../firebase';

/**
 * Fetch workspace document snapshots for the current user
 * @param {number} workspaceId - The workspace ID
 */
export const fetchWorkspaceSnapshots = async (workspaceId) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return [];
        }

        const token = await user.getIdToken();

        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/snapshots/list/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Auth-Token': `Bearer ${token}`,
                'Authorization': `${token}`,
                'Requesting-User-Uid': user.uid
            }
        });

        if (response.ok) {
            const result = await response.json();
            return result.snapshots || [];
        } else {
            console.error('Failed to fetch workspace snapshots:', response.status, response.statusText);
            return [];
        }
    } catch (error) {
        console.error('Error fetching workspace snapshots:', error);
        return [];
    }
};

/**
 * Save a snapshot of the current team workspace document state
 * @param {Object} documentData - The document data to save
 * @param {number} workspaceId - The workspace ID
 * @param {string} documentData.type - 'space' or 'page'
 * @param {string} documentData.name - Document name
 * @param {string} documentData.content - Document content
 * @param {string} documentData.description - Document description
 * @param {string} documentData.parentPage - Parent page (for pages only)
 * @param {boolean} documentData.isPublic - Whether document is public
 * @param {boolean} documentData.completed - Whether document is completed
 */
export const saveWorkspaceDocumentSnapshot = async (workspaceId, documentData) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return false;
        }

        const token = await user.getIdToken();

        // Prepare snapshot data
        const snapshotPayload = {
            document_type: documentData.type,
            document_name: documentData.name,
            content: documentData.content,
            description: documentData.description,
            is_public: documentData.isPublic,
            completed: documentData.completed,
        };

        // Add parent_page for pages
        if (documentData.type === 'page' && documentData.parentPage) {
            snapshotPayload.parent_page = documentData.parentPage;
        }

        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/snapshots/save/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Auth-Token': `Bearer ${token}`,
                'Authorization': `${token}`,
                'Requesting-User-Uid': user.uid
            },
            body: JSON.stringify(snapshotPayload)
        });

        if (response.ok) {
            await response.json();
            return true;
        } else {
            console.error('Failed to save team workspace snapshot:', response.status, response.statusText);
            return false;
        }
    } catch (error) {
        console.error('Error saving team workspace snapshot:', error);
        return false;
    }
};

/**
 * Custom hook for automatic team workspace snapshot saving
 * @param {number} workspaceId - The workspace ID
 * @param {Object} documentData - Current document data
 * @param {number} frequencyMinutes - How often to save snapshots (in minutes)
 * @param {boolean} enabled - Whether auto-saving is enabled
 */
export const useAutoWorkspaceSnapshot = (workspaceId, documentData, frequencyMinutes = 5, enabled = true) => {
    const intervalRef = React.useRef(null);
    const lastSnapshotRef = React.useRef(null);

    const startAutoSnapshot = React.useCallback(() => {
        if (!enabled || !documentData.name || !documentData.content || !workspaceId) {
            return;
        }

        // Clear existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Set up new interval
        intervalRef.current = setInterval(async () => {
            // Only save if content has changed since last snapshot
            const currentContent = JSON.stringify(documentData);
            if (currentContent !== lastSnapshotRef.current) {
                const success = await saveWorkspaceDocumentSnapshot(workspaceId, documentData);
                if (success) {
                    lastSnapshotRef.current = currentContent;
                }
            }
        }, frequencyMinutes * 60 * 1000); // Convert minutes to milliseconds

    }, [workspaceId, documentData, frequencyMinutes, enabled]);

    const stopAutoSnapshot = React.useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const saveManualSnapshot = React.useCallback(async () => {
        if (!documentData.name || !documentData.content || !workspaceId) {
            return false;
        }

        const success = await saveWorkspaceDocumentSnapshot(workspaceId, documentData);
        if (success) {
            lastSnapshotRef.current = JSON.stringify(documentData);
        }
        return success;
    }, [workspaceId, documentData]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return {
        startAutoSnapshot,
        stopAutoSnapshot,
        saveManualSnapshot
    };
};